import { ENTITY_STATUS } from '@monorepo/shared/schemas/common'
import type { EntityStatus } from '@monorepo/shared/schemas/common/enums'
import type { FeatureResponse } from '@monorepo/shared/schemas/features'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Download,
  Eye,
  MoreHorizontal,
  PauseCircle,
  Play,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { featureService } from '@/features/features/services/featureService'
import { PageTitle } from '@/shared/components/common/PageTitle'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { ConfirmationDialog } from '@/shared/components/ui/confirmation-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { toast } from '@/shared/lib/toast'
import { FeatureDrawer } from './FeatureDrawer'

export function FeaturesList() {
  const navigate = useNavigate()

  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | EntityStatus>('all')
  const [features, setFeatures] = useState<FeatureResponse[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [deleteFeature, setDeleteFeature] = useState<FeatureResponse | null>(
    null
  )
  const [isDeletingFeature, setIsDeletingFeature] = useState(false)
  const [showCreateDrawer, setShowCreateDrawer] = useState(false)

  const fetchFeatures = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await featureService.getFeatures({
        search: searchQuery || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      })

      // Data already matches FeatureResponse schema from backend
      setFeatures(response.data)
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'An unexpected error occurred'
      toast.error('Failed to fetch features', message)
    } finally {
      setIsLoading(false)
    }
  }, [searchQuery, statusFilter])

  // Fetch features on component mount and when filters change
  useEffect(() => {
    fetchFeatures()
  }, [fetchFeatures])

  const handleFeatureAction = async (
    action: string,
    feature: FeatureResponse
  ) => {
    switch (action) {
      case 'view': {
        navigate({
          to: '/features/$featureId',
          params: { featureId: feature.id },
        })
        break
      }
      case 'activate': {
        try {
          await featureService.updateFeature(feature.id, {
            status: ENTITY_STATUS.active,
          })
          setFeatures(prev =>
            prev.map(f =>
              f.id === feature.id ? { ...f, status: ENTITY_STATUS.active } : f
            )
          )
          toast.success('Feature activated successfully')
        } catch (error: unknown) {
          const message =
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred'
          toast.error('Failed to activate feature', message)
        }
        break
      }
      case 'deactivate': {
        try {
          await featureService.updateFeature(feature.id, {
            status: ENTITY_STATUS.draft,
          })
          setFeatures(prev =>
            prev.map(f =>
              f.id === feature.id ? { ...f, status: ENTITY_STATUS.draft } : f
            )
          )
          toast.success('Feature moved to draft successfully')
        } catch (error: unknown) {
          const message =
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred'
          toast.error('Failed to move feature to draft', message)
        }
        break
      }
      case 'delete': {
        setDeleteFeature(feature)
        break
      }
    }
  }

  const handleConfirmDelete = async () => {
    if (deleteFeature) {
      try {
        setIsDeletingFeature(true)
        const response = await featureService.deleteFeature(deleteFeature.id)
        setFeatures(prev => prev.filter(f => f.id !== deleteFeature.id))
        toast.success(response.message)
        setDeleteFeature(null)
      } catch (error: unknown) {
        const message =
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred'
        toast.error('Failed to delete feature', message)
      } finally {
        setIsDeletingFeature(false)
      }
    }
  }

  const handleCreateNew = () => {
    setShowCreateDrawer(true)
  }

  const handleRowClick = (feature: FeatureResponse) => {
    navigate({ to: '/features/$featureId', params: { featureId: feature.id } })
  }

  const getStatusVariant = (status: FeatureResponse['status']) => {
    return status === ENTITY_STATUS.active ? 'default' : 'secondary'
  }

  const columns: ColumnDef<FeatureResponse>[] = [
    {
      accessorKey: 'feature_name',
      header: 'Feature Name',
      cell: ({ row }) => {
        const feature = row.original
        return <div className='font-medium'>{feature.feature_name}</div>
      },
      size: 200,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const feature = row.original
        return (
          <Badge variant={getStatusVariant(feature.status)} className='text-xs'>
            {feature.status}
          </Badge>
        )
      },
      size: 100,
    },
    {
      accessorKey: 'billable_signal.signal_name',
      header: 'Billable Signal',
      cell: ({ row }) => {
        const feature = row.original
        if (!feature.billable_signal) {
          return <span className='text-gray-400'>-</span>
        }
        return (
          <div className='font-mono text-sm'>
            {feature.billable_signal.signal_name}
          </div>
        )
      },
      size: 150,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const feature = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='sm'>
                <MoreHorizontal className='w-4 h-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem
                onClick={() => handleFeatureAction('view', feature)}
              >
                <Eye className='w-4 h-4 mr-2' />
                View
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {feature.status === ENTITY_STATUS.draft && (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleFeatureAction('activate', feature)
                  }}
                >
                  <Play className='w-4 h-4 mr-2' />
                  Activate
                </DropdownMenuItem>
              )}
              {feature.status === ENTITY_STATUS.active && (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleFeatureAction('deactivate', feature)
                  }}
                >
                  <PauseCircle className='w-4 h-4 mr-2' />
                  Move to Draft
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation()
                  handleFeatureAction('delete', feature)
                }}
                className='text-red-600 focus:text-red-600'
              >
                <Trash2 className='w-4 h-4 mr-2' />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
      size: 50,
    },
  ]

  // Features are already filtered server-side
  const filteredFeatures = features

  // Calculate stats
  const totalFeatures = features.length
  const activeFeatures = features.filter(
    f => f.status === ENTITY_STATUS.active
  ).length
  const draftFeatures = features.filter(
    f => f.status === ENTITY_STATUS.draft
  ).length

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex flex-col gap-4 md:flex-row md:justify-between md:items-start'>
        <div>
          <PageTitle
            title='Features'
            subtitle='Manage your non-agent features and capabilities'
          />
          {/* Stats Chips */}
          <div className='flex flex-wrap gap-2 mt-3'>
            <Badge variant='secondary' className='text-sm'>
              {totalFeatures} Total
            </Badge>
            <Badge variant='default' className='text-sm'>
              {activeFeatures} Active
            </Badge>
            <Badge variant='secondary' className='text-sm'>
              {draftFeatures} Draft
            </Badge>
          </div>
        </div>
        <div className='flex gap-3 items-center w-full md:w-auto'>
          <Button
            onClick={handleCreateNew}
            size='lg'
            className='flex-1 md:flex-initial'
          >
            <Plus className='w-5 h-5 mr-2' />
            Create Feature
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
        <div className='flex gap-2 items-center flex-1 max-w-2xl'>
          <div className='relative flex-1'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4' />
            <Input
              placeholder='Search features...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className='pl-10'
            />
          </div>

          <Select
            value={statusFilter}
            onValueChange={value =>
              setStatusFilter(value as 'all' | EntityStatus)
            }
          >
            <SelectTrigger className='w-1/4'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Status</SelectItem>
              <SelectItem value={ENTITY_STATUS.active}>Active</SelectItem>
              <SelectItem value={ENTITY_STATUS.draft}>Draft</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className='flex gap-2 items-center'>
          <Button
            variant='outline'
            size='sm'
            onClick={fetchFeatures}
            disabled={isLoading}
          >
            <RefreshCw
              className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`}
            />
          </Button>

          <Button variant='outline' size='sm'>
            <Download className='w-4 h-4' />
          </Button>
        </div>
      </div>

      {/* Features Table */}
      <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
        <EnhancedTable
          key='features-table-simple'
          data={filteredFeatures}
          columns={columns}
          height='calc(100vh - 400px)'
          className='bg-transparent border-0'
          onRowClick={handleRowClick}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={!!deleteFeature}
        onOpenChange={open => !open && setDeleteFeature(null)}
        title='Delete Feature'
        description={`Are you sure you want to delete "${deleteFeature?.feature_name}"? This action cannot be undone and will permanently remove the feature and all its associated data.`}
        confirmText='Delete'
        cancelText='Cancel'
        variant='destructive'
        isLoading={isDeletingFeature}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteFeature(null)}
      />

      {/* Create Feature Drawer */}
      <FeatureDrawer
        open={showCreateDrawer}
        onOpenChange={setShowCreateDrawer}
      />
    </div>
  )
}
